import React from 'react';
import { Mail, MessageCircle, MapPin, ExternalLink, Linkedin } from 'lucide-react';

export const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center p-1">
                <img 
                  src="/images/greenflow-logo-new.png" 
                  alt="Greenflow Ventures"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold">Greenflow Ventures</h3>
              </div>
            </div>
            
            <p className="text-gray-300 mb-6 leading-relaxed">
              Strategic investment holding company providing comprehensive oversight, governance, and strategic direction for Credora Capital and other investment initiatives.
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-gray-300">
                <Mail className="w-4 h-4 text-emerald-400" />
                <a href="mailto:gp@greenflow.ventures" className="hover:text-emerald-400 transition-colors">
                  gp@greenflow.ventures
                </a>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <MapPin className="w-4 h-4 text-emerald-400" />
                <span>Daryaganj, Delhi, India</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <Linkedin className="w-4 h-4 text-emerald-400" />
                <a href="https://www.linkedin.com/company/greenflow-ventures-pvt-ltd/" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors">
                  LinkedIn
                </a>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <MessageCircle className="w-4 h-4 text-emerald-400" />
                <a href="https://wa.me/919455461460?text=Hello,%0A%0AI%20am%20interested%20in%20learning%20more%20about%20Credora%20Capital%20investment%20opportunities.%0A%0APlease%20assist%20with%20further%20details.%0A%0AThank%20you." target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors">
                  +91 9455461460 (WhatsApp)
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-gray-300 hover:text-emerald-400 transition-colors duration-200"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('services')}
                  className="text-gray-300 hover:text-emerald-400 transition-colors duration-200"
                >
                  Strategy
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('fund-details')}
                  className="text-gray-300 hover:text-emerald-400 transition-colors duration-200"
                >
                  Fund Details
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('team')}
                  className="text-gray-300 hover:text-emerald-400 transition-colors duration-200"
                >
                  Team
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-gray-300 hover:text-emerald-400 transition-colors duration-200"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Investment Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Investment Information</h4>
            <div className="space-y-4">
              <div className="bg-gray-800 rounded-lg p-4">
                <h5 className="font-semibold text-emerald-400 mb-2">Minimum Investment</h5>
                <p className="text-sm text-gray-300">₹1 Crore</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4">
                <h5 className="font-semibold text-emerald-400 mb-2">Fund Type</h5>
                <p className="text-sm text-gray-300">Category I AIF</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4">
                <h5 className="font-semibold text-emerald-400 mb-2">Target AUM</h5>
                <p className="text-sm text-gray-300">₹1000 Crores</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-400">
                © 2026 Greenflow Ventures. All rights reserved.
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-400">Sponsored by</span>
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center p-1">
                  <img 
                    src="/images/greenflow-logo-new.png" 
                    alt="Greenflow Ventures Pvt Limited"
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-sm text-gray-400">Greenflow Ventures Pvt Limited</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-400">
              <span className="flex items-center space-x-1">
                <span>Regulated by SEBI</span>
                <ExternalLink className="w-3 h-3" />
              </span>
              <span>Category I AIF</span>
            </div>
          </div>
          
          <div className="mt-4 text-xs text-gray-500 text-center md:text-left">
            <p className="mb-2">
              <strong>Disclaimer:</strong> This website is for informational purposes only and does not constitute an offer to sell or solicitation to buy securities. 
              Past performance is not indicative of future results. Investments in AIFs are subject to market risks.
            </p>
            <p>
              Please read the Private Placement Memorandum carefully before investing. Consult your financial advisor before making investment decisions.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};